import { type ComponentChildren } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import * as stylex from '@stylexjs/stylex';
import { ChevronRightIcon } from '@xantory/icons';
import { styles } from './sidebar.styles';
import { portalTarget } from '../../ThemeProvider';
import { activeSectionTitle, bestMatchKey, sectionKeys } from './nav-model';
export interface NavLeaf {
  key: string;
  label: string;
  href: string;
}
export type NavNode = {
  kind: 'leaf';
  key: string;
  label: string;
  icon: ComponentChildren;
  href: string;
} | {
  kind: 'group';
  key: string;
  label: string;
  icon: ComponentChildren;
  children: NavLeaf[];
};
export interface NavSection {
  title: string;
  /** The section's own glyph — the collapsed rail shows ONE icon per section.
   *  Absent ⇒ the rail falls back to the section's first item's icon. */
  icon?: ComponentChildren;
  cloudOnly?: boolean;
  items: NavNode[];
}
function groupKeys(sections: NavSection[]): string[] {
  return sections.flatMap(s => s.items.filter(i => i.kind === 'group').map(i => i.key));
}
interface NavTreeProps {
  sections: NavSection[];
  activeKey: string;
  onNavigate?: (() => void) | undefined;
  /** Live counts by nav key (e.g. unread notifications) — the app layer owns the
   *  data, the tree only renders it. Absent / 0 renders nothing. */
  badges?: Record<string, number> | undefined;
}

/** Counts are a glance signal, not a readout — past 99 the exact number stops
 *  mattering and would break the pill's width. */
function badgeText(n: number): string {
  return n > 99 ? '99+' : String(n);
}

/** The expanded tree. ⚠ Must MOUNT on every rail → full switch (Sidebar renders it
 *  and `NavRail` as siblings, never one component toggling between them): the
 *  scroll-to-active effect below is mount-only, and when the rail branch lived in
 *  here behind an early return, re-expanding reused the instance, the effect never
 *  re-ran, and the list opened at the top with the current page below the fold. */
export function NavTree({
  sections,
  activeKey,
  onNavigate,
  badges
}: NavTreeProps) {
  const activeLeaf = bestMatchKey(sections, activeKey);
  const [open, setOpen] = useState<Set<string>>(() => new Set(groupKeys(sections)));
  const toggle = (key: string) => setOpen(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key);else next.add(key);
    return next;
  });

  // `.scroll-hidden` is plain CSS in reset.css: StyleX has no typed API for the
  // non-standard ::-webkit-scrollbar. Merged into `className` (what stylex.props
  // actually returns) rather than `class`, which would silently drop it.
  // R-346 — after a refresh the nav's own scroll position starts at 0, so a deep
  // route (Admin → Sites) rendered its active row BELOW the fold and the sidebar
  // looked like it had selected the first section. Selection was always right; it
  // was simply off-screen. Runs once on mount — not on every `activeKey` change,
  // because clicking a visible row must not yank the list around under the cursor.
  const navEl = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!activeLeaf) return;
    const row = navEl.current?.querySelector(`[data-nav-key="${CSS.escape(activeLeaf)}"]`);
    row?.scrollIntoView({
      block: 'center'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only, by design
  }, []);
  const navProps = stylex.props(styles.nav);
  return <nav ref={navEl} {...navProps} className={[navProps.className, 'scroll-hidden'].filter(Boolean).join(' ')}>
      {sections.map(sec => <div key={sec.title} {...stylex.props(styles.navSec)}>
          <div {...stylex.props(styles.navGroup)}>{sec.title}</div>
          <div {...stylex.props(styles.navSecBody)}>
            {sec.items.map((node, i) => {
          const last = i === sec.items.length - 1;
          if (node.kind === 'leaf') {
            return <LeafRow key={node.key} node={node} active={node.key === activeLeaf} last={last} onNavigate={onNavigate} badge={badges?.[node.key] ?? 0} />;
          }
          return <GroupRow key={node.key} node={node} activeKey={activeLeaf ?? ''} open={open.has(node.key)} last={last} onToggle={() => toggle(node.key)} onNavigate={onNavigate} badges={badges} />;
        })}
          </div>
        </div>)}
    </nav>;
}
function LeafRow({
  node,
  active,
  last,
  onNavigate,
  badge
}: {
  node: Extract<NavNode, {
    kind: 'leaf';
  }>;
  active: boolean;
  last: boolean;
  onNavigate?: (() => void) | undefined;
  badge: number;
}) {
  return <a href={node.href} data-nav-key={node.key} onClick={onNavigate} aria-current={active ? 'page' : undefined} {...stylex.props(styles.treeRow, styles.connector, last ? styles.elbow : styles.trunkSeg, active && styles.treeRowActive)}>
      <span {...stylex.props(styles.navIcon, active && styles.navIconActive)}>{node.icon}</span>
      <span {...stylex.props(styles.navLabel)}>{node.label}</span>
      {/* Not aria-hidden: the count belongs in the link's accessible name, so a
          screen reader hears "Notifications 3" rather than just "Notifications". */}
      {badge > 0 && <span {...stylex.props(styles.navBadge)}>{badgeText(badge)}</span>}
    </a>;
}
function GroupRow({
  node,
  activeKey,
  open,
  last,
  onToggle,
  onNavigate,
  badges
}: {
  node: Extract<NavNode, {
    kind: 'group';
  }>;
  activeKey: string;
  open: boolean;
  last: boolean;
  onToggle: () => void;
  onNavigate?: (() => void) | undefined;
  /** Counts by leaf key — a CHILD can carry one too (the assets queue does), not
   *  just a top-level leaf. Rendering these on leaves ONLY meant a caller could
   *  wire a badge for a child and have it silently do nothing, which is worse than
   *  not supporting it at all. */
  badges?: Record<string, number> | undefined;
}) {
  const childActive = node.children.some(c => c.key === activeKey);
  // Trunk continuity: the header row draws its own segment (elbow if last, else a full-height
  // trunkSeg). A NON-last group also has its navSub children below the header — the header's
  // segment can't cover that, so navParent draws its own full-height trunk (navParentTrunk) to
  // bridge the gap down to the next sibling. A last group needs neither (elbow terminates it).
  return <div {...stylex.props(styles.navParent, !last && styles.navParentTrunk)}>
      {/* button, not a bare <a> — an href-less anchor is unfocusable, making
          the group toggle mouse-only. */}
      <button type="button" aria-expanded={open} {...stylex.props(styles.treeRow, styles.groupBtn, styles.connector, last ? styles.elbow : styles.trunkSeg, childActive && !open && styles.treeRowActive)} onClick={onToggle}>
        <span {...stylex.props(styles.navIcon)}>{node.icon}</span>
        <span {...stylex.props(styles.navLabel)}>{node.label}</span>
        <span {...stylex.props(styles.chev, open && styles.chevOpen)}>
          <ChevronRightIcon strokeWidth={2.2} />
        </span>
      </button>
      <div {...stylex.props(styles.navSub, open && styles.navSubOpen)}>
        {node.children.map((c, j) => {
        const childLast = j === node.children.length - 1;
        return <a key={c.key} href={c.href} data-nav-key={c.key} onClick={onNavigate} aria-current={c.key === activeKey ? 'page' : undefined} {...stylex.props(styles.treeRow, styles.connector, styles.subText, childLast ? styles.elbow : styles.trunkSeg, c.key === activeKey && styles.treeRowActive)}>
              <span {...stylex.props(styles.navLabel)}>{c.label}</span>
              {/* Not aria-hidden, same reasoning as the leaf row: the count
                  belongs in the link's accessible name. */}
              {(badges?.[c.key] ?? 0) > 0 && <span {...stylex.props(styles.navBadge)}>{badgeText(badges?.[c.key] ?? 0)}</span>}
            </a>;
      })}
      </div>
    </div>;
}

/** Where a section's flyout sits: beside its rail icon, growing DOWN from the icon in
 *  the top half of the screen and UP from it in the bottom half, so Administration's
 *  eight pages never run off a short tablet screen. `left` is the rail's own width. */
type FlyoutAnchor = {
  title: string;
  top?: number;
  bottom?: number;
  maxHeight: number;
};
function anchorFor(title: string, r: DOMRect): FlyoutAnchor {
  const vh = window.innerHeight;
  return r.top > vh / 2 ? {
    title,
    bottom: vh - r.bottom,
    maxHeight: r.bottom - 8
  } : {
    title,
    top: r.top,
    maxHeight: vh - r.top - 8
  };
}

/** The collapsed rail: ONE icon per section (user-set 2026-09-30 — every page as its
 *  own icon made ~30 indistinguishable glyphs). The section holding the current page
 *  is lit; clicking an icon opens that section's pages in a flyout beside it, so every
 *  page stays one click away without expanding the sidebar. */
export function NavRail({
  sections,
  activeKey,
  onNavigate,
  badges
}: NavTreeProps) {
  const activeLeaf = bestMatchKey(sections, activeKey);
  const activeTitle = activeSectionTitle(sections, activeKey);
  // R-346 — a styled hover label, replacing `title=`. The native tooltip waits
  // about a second and cannot be styled, which on an icon-only rail is the
  // difference between a usable nav and guesswork.
  const [hovered, setHovered] = useState<{
    label: string;
    top: number;
  } | null>(null);
  const [flyout, setFlyout] = useState<FlyoutAnchor | null>(null);
  const railEl = useRef<HTMLElement | null>(null);
  const flyEl = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!activeTitle) return;
    railEl.current?.querySelector(`[data-nav-section="${CSS.escape(activeTitle)}"]`)?.scrollIntoView({
      block: 'center'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only, by design
  }, []);

  // A navigation (link click, back button, ⌘K) closes it.
  useEffect(() => setFlyout(null), [activeKey]);
  const flyoutTitle = flyout?.title;
  useEffect(() => {
    if (!flyoutTitle) return;
    // Keyboard reach: the flyout is portaled to the END of <body>, so Tab from the
    // icon would never arrive in it — move focus to its first link instead.
    flyEl.current?.querySelector<HTMLElement>('a')?.focus();
    const trigger = () => railEl.current?.querySelector<HTMLElement>(`[data-nav-section="${CSS.escape(flyoutTitle)}"]`);
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      // The rail itself is excluded so a click on the same icon toggles it shut
      // (its own onClick) instead of closing here and reopening there.
      if (railEl.current?.contains(t) || flyEl.current?.contains(t)) return;
      setFlyout(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setFlyout(null);
      trigger()?.focus();
    };
    // A fixed panel does not follow its anchor — close rather than float detached.
    const onMove = () => setFlyout(null);
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onMove);
    railEl.current?.addEventListener('scroll', onMove);
    const rail = railEl.current;
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onMove);
      rail?.removeEventListener('scroll', onMove);
    };
  }, [flyoutTitle]);
  const count = (key: string) => badges?.[key] ?? 0;
  const openSec = flyout ? sections.find(s => s.title === flyout.title) : undefined;
  const close = () => {
    setFlyout(null);
    onNavigate?.();
  };
  return <nav ref={railEl} {...stylex.props(styles.nav, styles.navRail)}>
      {sections.map(sec => {
      const active = sec.title === activeTitle;
      const open = flyout?.title === sec.title;
      const badge = sectionKeys(sec).reduce((n, k) => n + count(k), 0);
      // Icon-only row: the label is the only place the count can be read, so it
      // carries the number the dot only hints at.
      const label = badge > 0 ? `${sec.title} (${badgeText(badge)})` : sec.title;
      const showTip = (e: Event) => {
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setHovered({
          label,
          top: r.top + r.height / 2
        });
      };
      return <button key={sec.title} type="button" data-nav-section={sec.title} aria-label={label} aria-expanded={open} aria-current={active ? 'true' : undefined} onMouseEnter={showTip} onMouseLeave={() => setHovered(null)} onFocus={showTip} onBlur={() => setHovered(null)} onClick={e => {
        setHovered(null);
        const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
        setFlyout(open ? null : anchorFor(sec.title, r));
      }} {...stylex.props(styles.railItem, styles.railButton, badge > 0 && styles.railItemRelative, (active || open) && styles.railItemActive)}>
            <span {...stylex.props(styles.navIcon, active && styles.navIconActive)}>
              {sec.icon ?? sec.items[0]?.icon}
            </span>
            {badge > 0 && <span aria-hidden="true" {...stylex.props(styles.railBadgeDot)} />}
          </button>;
    })}
      {/* Both portaled to body: the shell's grid clips overflow, and that clips a
          DESCENDANT's fixed/absolute paint at its box whatever the z-index. */}
      {hovered && !flyout && typeof document !== 'undefined' && createPortal(<span role="presentation" {...stylex.props(styles.railTooltip)} style={{
      left: '74px',
      top: `${hovered.top}px`,
      transform: 'translateY(-50%)'
    }}>
            {hovered.label}
          </span>, portalTarget())}
      {flyout && openSec && typeof document !== 'undefined' && createPortal(<nav ref={flyEl} aria-label={openSec.title} {...stylex.props(styles.railFlyout)} style={{
      left: '74px',
      top: flyout.top != null ? `${flyout.top}px` : undefined,
      bottom: flyout.bottom != null ? `${flyout.bottom}px` : undefined,
      maxHeight: `${flyout.maxHeight}px`
    }}>
            <div {...stylex.props(styles.navGroup, styles.railFlyoutTitle)}>{openSec.title}</div>
            {openSec.items.map(node => node.kind === 'leaf' ? <FlyoutLink key={node.key} href={node.href} label={node.label} icon={node.icon} active={node.key === activeLeaf} badge={count(node.key)} onClick={close} /> : <div key={node.key}>
                  <div {...stylex.props(styles.railFlyoutGroup)}>
                    <span {...stylex.props(styles.navIcon)}>{node.icon}</span>
                    <span {...stylex.props(styles.navLabel)}>{node.label}</span>
                  </div>
                  {node.children.map(c => <FlyoutLink key={c.key} href={c.href} label={c.label} indent active={c.key === activeLeaf} badge={count(c.key)} onClick={close} />)}
                </div>)}
          </nav>, portalTarget())}
    </nav>;
}
function FlyoutLink({
  href,
  label,
  icon,
  indent = false,
  active,
  badge,
  onClick
}: {
  href: string;
  label: string;
  icon?: ComponentChildren;
  indent?: boolean;
  active: boolean;
  badge: number;
  onClick: () => void;
}) {
  return <a href={href} onClick={onClick} aria-current={active ? 'page' : undefined} {...stylex.props(styles.treeRow, indent && styles.railFlyoutIndent, active && styles.treeRowActive)}>
      {icon != null && <span {...stylex.props(styles.navIcon, active && styles.navIconActive)}>{icon}</span>}
      <span {...stylex.props(styles.navLabel)}>{label}</span>
      {/* In the link's accessible name, same as the tree rows. */}
      {badge > 0 && <span {...stylex.props(styles.navBadge)}>{badgeText(badge)}</span>}
    </a>;
}